import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { getNavMenuLinks } from './nav-bar.config';
import { UserService } from '../services/user.service';
import { NavBarModelConfig } from './models/nav-bar.model.config';
import { ConfigService } from '../services/config.service';
import { EntraConfigService } from '../services/entra-config.service';
import { ModalService } from 'src/app/shared/components/modal/modal.service';
import { URL_PATHS } from 'src/app/shared/constants/constants';
import { CONTENT } from './util/content';
import { Privileges } from "../../shared/constants/privileges";
import { Constants, SsoApplicationType } from 'src/app/shared/constants/constants';
import { APP_CONFIG } from '../services/app-config.service';
import { getActiveAdGroups } from '../../modules/login/util/constants';
import { NgClass, UpperCasePipe } from '@angular/common';

@Component({
    selector: 'app-nav-bar',
    templateUrl: './nav-bar.component.html',
    styleUrls: ['./nav-bar.component.scss'],
    imports: [NgClass, RouterLink, UpperCasePipe]
})
export class NavBarComponent implements OnInit, OnDestroy {

  navMenu: NavBarModelConfig[] = getNavMenuLinks();
  content = CONTENT;
  // Array to store subscriptions for cleanup on component destruction
  subscriptions: Subscription[] = [];
  spoofWindow: any;
  ifpLinkEnabled: boolean = false;

  constructor(
    public user: UserService,
    private config: ConfigService,
    private entraConfig: EntraConfigService,
    private modalService: ModalService, private router: Router
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.user.isAuntheticated$
        .subscribe((isAuth: boolean) => {
          if (isAuth) {
            this.ifpLinkEnabled = APP_CONFIG.IFP_ENROLLMENT_LINK_ENABLED === 'true';
            
            // POC – Entra SSO: Use AD group-based config if user logged in via Azure AD
            const isAzureLogin = localStorage.getItem('ENABLE_AZURE_LOGIN') === 'true';
            
            if (isAzureLogin) {
              this.entraConfig.updateConfig(this.navMenu);
              this.navMenu = this.entraConfig.configData;
            } else {
              this.config.updateConfig(this.navMenu);
              this.navMenu = this.config.configData;
            }
          }
        })
    );
  }

  /**
   * Calls the given method
   */
  onClick(methodName: string) {
    (this as { [key: string]: any })[methodName]();
  }

  /**
   * Redirects to whyzen sso or opens a pop-up with user access levels
   */
  callWhyzenSso() {
    if (this.user.getAccessLevels.length > 1) {
      this.modalService.open('whyzen-access-modal', false);
    } else {
      window.open(URL_PATHS.WHYZEN_SSO + this.user.getAccessLevels[0], '_blank');
    }
  }

  /**
  * Redirects to provider activity splunk login
  */
  callProviderActivitySplunk() {
    window.open(APP_CONFIG.SPLUNK_AUDIT_PROVIDER_ACTIVITY_REPORT, '_blank');
  }

  /**
  * Redirects to member activity splunk login
  */
  callMemberActivitySplunk() {
    window.open(APP_CONFIG.SPLUNK_AUDIT_MEMBER_ACTIVITY_REPORT, '_blank');
  }

  /**
  * Redirects to employer activity splunk login
  */
  callEmployerActivitySplunk() {
    window.open(APP_CONFIG.SPLUNK_AUDIT_EMPLOYER_ACTIVITY_REPORT, '_blank');
  }

  /**
  * Redirects to prospect activity splunk login
  */
  callProspectActivitySplunk() {
    window.open(APP_CONFIG.SPLUNK_AUDIT_PROSPECT_ACTIVITY_REPORT, '_blank');
  }

  /**
   * Method to route to spoof screen
   */
  spoofBrokerConnection(userName: string) {
    // if (userRole === 'BROKER') {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([Constants.SSO_REDIRECTION_FULL_PATH], {
        queryParams: {
          sso: SsoApplicationType.PRODUCER,
          spoofId: '1234',
          username: userName
        }
      })
    );
    // Logout the previous spoofed user and login with new spoof user
    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        const xhr2 = new XMLHttpRequest();
        xhr2.onreadystatechange = () => {
          if (xhr2.readyState === 4) {
            let spoofWindow = window.open(APP_CONFIG.DOMAIN + '/bwa/spoof/pa/oidc/logout', 'mywindow');
            spoofWindow?.close();
            spoofWindow = window.open(url, 'mywindow');
          }
        };
        xhr2.open('GET', APP_CONFIG.DOMAIN + '/bwa/pf/idp/startSLO.ping', true);
        xhr2.send();
      }
    };
    xhr.open('GET', APP_CONFIG.DOMAIN + '/pa/oidc/logout', true);
    xhr.send();
  }

  /** True when the user authenticated via Azure AD (Entra SSO flow) */
  get isAzureLoginEnabled(): boolean {
    return localStorage.getItem('ENABLE_AZURE_LOGIN') === 'true';
  }

  /** Resolves the Producer DSA AD group string for the current environment */
  get producerDsaGroup(): string {
    return getActiveAdGroups().PRODUCER_DSA;
  }

  /** Resolves the Producer REP AD group string for the current environment */
  get producerRepGroup(): string {
    return getActiveAdGroups().PRODUCER_REP;
  }

  /** Resolves the Super Admin AD group string for the current environment */
  get superAdminGroup(): string {
    return getActiveAdGroups().SUPER_ADMIN;
  }

  /** Resolves the Prospect REP AD group string for the current environment */
  get prospectRepGroup(): string {
    return getActiveAdGroups().PROSPECT_REP;
  }

  /** Returns true if the user belongs to the Producer DSA AD group */
  hasProducerDsaAccess(): boolean {
    return this.hasAdGroupAccess(this.producerDsaGroup);
  }

  /** Returns true if the user belongs to the Producer REP AD group */
  hasProducerRepAccess(): boolean {
    return this.hasAdGroupAccess(this.producerRepGroup);
  }

  /** Returns true if the user belongs to the Super Admin AD group */
  hasSuperAdminAccess(): boolean {
    return this.hasAdGroupAccess(this.superAdminGroup);
  }

  /** Returns true if the user belongs to the Prospect REP AD group */
  hasProspectRepAccess(): boolean {
    return this.hasAdGroupAccess(this.prospectRepGroup);
  }

  /** Checks Azure login is active and the user's AD groups include the given group */
  private hasAdGroupAccess(group: string): boolean {
    return this.isAzureLoginEnabled && this.user.getAdGroups.includes(group);
  }

  // Method to unsubscribe from subscriptions on component destruction
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

}
